import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Folder as FolderIcon, ChevronRight, Pencil, Trash2, Download } from "lucide-react";
import { promptDialog } from "@/components/PromptDialog";
import { confirmDialog } from "@/components/ConfirmDialog";
import { downloadAssetsZip } from "@/lib/download";

/**
 * FolderTile — clickable + drop target.
 * Props:
 *  - folder: {id, name, asset_count, folder_count}
 *  - onOpen(folderId) — navigate into
 *  - onRename(folderId, newName) — persist rename
 *  - onDelete(folderId)
 *  - onDropAsset(assetId, folderId) — an asset was dropped into this folder
 *  - canManage — bool
 */
export default function FolderTile({ folder, onOpen, onRename, onDelete, onDropAsset, canManage }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(folder.name);
  const [dragOver, setDragOver] = useState(false);

  function commitRename() {
    setEditing(false);
    const n = name.trim();
    if (!n || n === folder.name) {
      setName(folder.name);
      return;
    }
    onRename && onRename(folder.id, n);
  }

  function handleDrop(e) {
    e.preventDefault();
    setDragOver(false);
    const assetId = e.dataTransfer.getData("application/x-apollo-asset-id");
    if (assetId) onDropAsset && onDropAsset(assetId, folder.id);
  }

  return (
    <button
      type="button"
      onClick={() => !editing && onOpen && onOpen(folder.id)}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      className={`group relative border rounded-sm overflow-hidden bg-surface hover-lift flex flex-col text-left aspect-[4/3] transition ${
        dragOver ? "border-foreground shadow-[0_0_0_2px_var(--foreground)] bg-surface-elev" : "border-border"
      }`}
      data-testid={`folder-tile-${folder.id}`}
    >
      <div className="flex-1 flex items-center justify-center bg-muted/40">
        <FolderIcon className="h-12 w-12 text-muted-foreground group-hover:text-foreground transition" style={{ color: dragOver ? "#FBC90C" : undefined }} />
      </div>
      <div className="p-3 border-t border-border">
        {editing ? (
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={commitRename}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitRename();
              if (e.key === "Escape") { setName(folder.name); setEditing(false); }
            }}
            onClick={(e) => e.stopPropagation()}
            className="w-full bg-transparent border-b border-foreground text-sm outline-none"
            data-testid={`folder-rename-${folder.id}`}
          />
        ) : (
          <div className="flex items-center justify-between gap-2">
            <div className="text-sm font-semibold truncate">{folder.name}</div>
            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          </div>
        )}
        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
          {folder.folder_count > 0 && <span>{folder.folder_count} folders · </span>}
          {folder.asset_count} assets
        </div>
      </div>
      {canManage && !editing && (
        <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition">
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              downloadAssetsZip({ folder_id: folder.id, name: folder.name });
            }}
            className="p-1.5 rounded-sm bg-white text-black cursor-pointer"
            data-testid={`folder-download-${folder.id}`}
            aria-label="Download folder as zip"
            title="Download folder as zip"
          >
            <Download className="h-3 w-3" />
          </span>
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); setEditing(true); }}
            className="p-1.5 rounded-sm bg-white text-black cursor-pointer"
            data-testid={`folder-edit-${folder.id}`}
            aria-label="Rename folder"
          >
            <Pencil className="h-3 w-3" />
          </span>
          <span
            role="button"
            tabIndex={0}
            onClick={async (e) => {
              e.stopPropagation();
              const ok = await confirmDialog({
                title: `Delete folder "${folder.name}"?`,
                description: "Assets and sub-folders inside will move up one level. This cannot be undone.",
                confirmLabel: "Delete Folder",
              });
              if (ok) onDelete && onDelete(folder.id);
            }}
            className="p-1.5 rounded-sm text-white cursor-pointer"
            style={{ background: "#E15C2C" }}
            data-testid={`folder-delete-${folder.id}`}
            aria-label="Delete folder"
          >
            <Trash2 className="h-3 w-3" />
          </span>
        </div>
      )}
      {!canManage && !editing && folder.asset_count > 0 && (
        <span
          role="button"
          tabIndex={0}
          onClick={(e) => {
            e.stopPropagation();
            downloadAssetsZip({ folder_id: folder.id, name: folder.name });
          }}
          className="absolute top-2 right-2 p-1.5 rounded-sm bg-white text-black opacity-0 group-hover:opacity-100 transition cursor-pointer"
          data-testid={`folder-download-${folder.id}`}
          aria-label="Download folder as zip"
          title="Download folder as zip"
        >
          <Download className="h-3 w-3" />
        </span>
      )}
    </button>
  );
}

export async function createFolderPrompt({ category, parentId }) {
  const name = await promptDialog({
    eyebrow: "New Folder",
    title: "Name this folder",
    placeholder: "e.g. Summer 2026",
    submitLabel: "Create Folder",
  });
  if (!name) return null;
  try {
    const { data } = await api.post("/admin/folders", { name, category, parent_id: parentId || null });
    toast.success(`Folder "${data.name}" created`);
    return data;
  } catch (err) {
    toast.error(formatApiError(err));
    return null;
  }
}
